import { Component, createContext, useContext, 
// @ts-ignore-next-line
// eslint-disable-next-line @typescript-eslint/no-unused-vars
__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED as ReactInternals, createElement, } from "react";
const currentStoreContainer = [];
const registeredProvidedStores = new Set();
export function getActiveStoreContainer() {
    return currentStoreContainer[currentStoreContainer.length - 1];
}
class StoreContainer {
    get isDisposed() {
        return this._isDisposed;
    }
    constructor(ref, constr, _parent) {
        this._parent = _parent;
        this._disposers = new Set();
        this._isDisposed = false;
        this._state = {
            isResolved: false,
            ref,
            constr,
        };
    }
    registerCleanup(cleaner) {
        this._disposers.add(cleaner);
    }
    resolve(store) {
        if (this._resolvementError) {
            throw this._resolvementError;
        }
        if (this._state.isResolved && store === this._state.ref) {
            return this._state.value;
        }
        if (!this._state.isResolved && this._state.ref === store) {
            try {
                currentStoreContainer.push(this);
                this._state = {
                    isResolved: true,
                    value: this._state.constr(),
                    ref: store,
                };
                currentStoreContainer.pop();
                return this._state.value;
            }
            catch (e) {
                this._resolvementError =
                    new Error(`Could not initialize store "${store === null || store === void 0 ? void 0 : store.name}":
${String(e)}`);
                throw this._resolvementError;
            }
        }
        if (!this._parent) {
            throw new Error(`The store "${store.name}" is not provided`);
        }
        return this._parent.resolve(store);
    }
    clear() {
        this._disposers.forEach((cleaner) => {
            cleaner();
        });
    }
    dispose() {
        this.clear();
        this._isDisposed = true;
    }
}
const storeContainerContext = createContext(null);
export class StoreContainerProvider extends Component {
    componentWillUnmount() {
        this.container.dispose();
    }
    render() {
        // React can keep the component reference and mount/unmount it multiple times. Because of that
        // we need to ensure to always have a hooks container instantiated when rendering, as it could
        // have been disposed due to an unmount
        if (!this.container || this.container.isDisposed) {
            this.container = new StoreContainer(this.props.store, () => this.props.store(this.props.props), 
            // eslint-disable-next-line
            // @ts-ignore
            this.context);
        }
        return createElement(storeContainerContext.Provider, {
            value: this.container,
        }, this.props.children);
    }
}
StoreContainerProvider.contextType = storeContainerContext;
export function cleanup(cleaner) {
    const activeStoreContainer = getActiveStoreContainer();
    // We do not want to clean up if we are not in a context, which
    // means we are just globally running the store
    if (!activeStoreContainer) {
        return;
    }
    activeStoreContainer.registerCleanup(cleaner);
}
export const componentConsumptionHooks = {
    isConsuming: false,
    onConsume: () => { },
    onConsumed: () => { },
};
const globalStores = new Map();
export function useStore(store) {
    const activeStoreContainer = getActiveStoreContainer();
    if (!activeStoreContainer) {
        if (!componentConsumptionHooks.isConsuming) {
            componentConsumptionHooks.isConsuming = true;
            componentConsumptionHooks.onConsume();
        }
        const storeContainer = useContext(storeContainerContext);
        if (!storeContainer) {
            let contextContainer = globalStores.get(store);
            if (!contextContainer && registeredProvidedStores.has(store)) {
                throw new Error(`The store ${store.name} should be provided on a context, but no provider was found`);
            }
            if (!contextContainer) {
                // @ts-ignore
                contextContainer = store();
                globalStores.set(store, contextContainer);
            }
            return contextContainer;
        }
        return storeContainer.resolve(store);
    }
    return activeStoreContainer.resolve(store);
}
export function createStoreProvider(store) {
    registeredProvidedStores.add(store);
    const StoreProvider = (props) => {
        // To avoid TSLIB
        const extendedProps = Object.assign({}, props);
        const children = extendedProps.children;
        delete extendedProps.children;
        return createElement(StoreContainerProvider, 
        // @ts-ignore
        {
            props: extendedProps,
            store,
        }, children);
    };
    StoreProvider.displayName = store.name
        ? `${store.name}Provider`
        : "AnonymousStoreProvider";
    StoreProvider.provide = (component) => {
        return (props) => {
            return createElement(StoreProvider, 
            // @ts-ignore
            props, 
            // @ts-ignore
            createElement(component, props));
        };
    };
    return StoreProvider;
}
if (typeof window !== "undefined") {
    let currentDispatcher = null;
    Object.defineProperty(ReactInternals.ReactCurrentDispatcher, "current", {
        get() {
            return currentDispatcher;
        },
        set(nextDispatcher) {
            currentDispatcher = nextDispatcher;
            if (componentConsumptionHooks.isConsuming &&
                // When the hooks has the same implementation, it is to throw an error, meaning
                // we are done consuming a hooks context
                currentDispatcher.useReducer === currentDispatcher.useEffect) {
                componentConsumptionHooks.isConsuming = false;
                componentConsumptionHooks.onConsumed();
            }
        },
    });
}
//# sourceMappingURL=store.js.map